import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/Login.css";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth, AuthService } from "../lib/authService";

// Wave Animation Component
const WaveAnimation = () => {
  return (
    <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden">
      <motion.div
        className="relative h-[300px]"
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
      >
        {/* Glow effect at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#72288D]/20 to-transparent" />

        {/* First wave - primary purple */}
        <motion.div
          className="absolute bottom-[-10%] h-[300px] w-[200%]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' opacity='.25' fill='%2372288D'%3E%3C/path%3E%3C/svg%3E\")",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          animate={{
            x: [0, -500],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Second wave - secondary purple */}
        <motion.div
          className="absolute bottom-[-20%] h-[300px] w-[200%]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' opacity='.2' fill='%236720A3'%3E%3C/path%3E%3C/svg%3E\")",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          animate={{
            x: [-500, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Third wave - tertiary purple */}
        <motion.div
          className="absolute bottom-[-30%] h-[300px] w-[200%]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' opacity='.3' fill='%236D2398'%3E%3C/path%3E%3C/svg%3E\")",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          animate={{
            x: [0, -600],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Fourth wave - accent purple */}
        <motion.div
          className="absolute bottom-[-40%] h-[300px] w-[200%]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' opacity='.15' fill='%23843866'%3E%3C/path%3E%3C/svg%3E\")",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          animate={{
            x: [-600, 0],
          }}
          transition={{
            duration: 35,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Animated purple dust particles on waves */}
        <div className="absolute inset-0">
          {Array.from({ length: 30 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-1 w-1 rounded-full bg-[#72288D]"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${50 + Math.random() * 50}%`,
                boxShadow: "0 0 5px 1px rgba(114, 40, 141, 0.7)",
              }}
              animate={{
                opacity: [0, 1, 0],
                y: [0, -20],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 2 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

// Define particle types with purple color variations
const particleTypes = [
  { icon: "♪", size: "text-sm", color: "text-[#72288D]/60" },
  { icon: "♫", size: "text-lg", color: "text-[#6720A3]/60" },
  { icon: "♩", size: "text-base", color: "text-[#6D2398]/50" },
  { icon: "♬", size: "text-xl", color: "text-[#72288D]/40" },
  { icon: "✦", size: "text-xs", color: "text-[#843866]/70" },
  { icon: "✧", size: "text-xs", color: "text-[#4A4A4A]/50" },
  { icon: "✶", size: "text-sm", color: "text-[#6720A3]/50" },
  { icon: "⋆", size: "text-xs", color: "text-[#6D2398]/60" },
  { icon: "⊹", size: "text-base", color: "text-[#4A4A4A]/40" },
  { icon: "★", size: "text-sm", color: "text-[#72288D]/60" },
  { icon: "✵", size: "text-xs", color: "text-[#843866]/70" },
];

// Particle Background Component
const ParticleBackground = () => {
  // Memoize the particles array so it doesn't change on re-renders
  const particles = useMemo(
    () =>
      Array.from({ length: 80 }).map((_, i) => {
        const type =
          particleTypes[Math.floor(Math.random() * particleTypes.length)];
        return {
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          delay: Math.random() * 5,
          duration: 15 + Math.random() * 30,
          type,
        };
      }),
    [],
  ); // Empty dependency array means this will only run once

  // Memoize the flare particles
  const flareParticles = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, i) => {
        const colors = ["#72288D", "#6720A3", "#6D2398", "#843866"];
        const color = colors[i % colors.length];
        return {
          id: i,
          x: 10 + Math.random() * 80,
          y: 10 + Math.random() * 80,
          size: 3 + Math.random() * 6,
          color,
        };
      }),
    [],
  ); // Empty dependency array means this will only run once

  // Memoize the lens flares
  const lensFlares = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, i) => {
        const colors = ["#72288D", "#6720A3", "#6D2398"];
        const color = colors[i % colors.length];
        return {
          id: i,
          x: 20 + Math.random() * 60,
          y: 20 + Math.random() * 60,
          width: 100 + Math.random() * 200,
          height: 10 + Math.random() * 20,
          rotation: Math.random() * 180,
          color,
        };
      }),
    [],
  ); // Empty dependency array means this will only run once

  return (
    <div className="absolute inset-0 z-0 overflow-hidden">
      {/* Purple dust particles */}
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className={`absolute ${particle.type.size} ${particle.type.color} pointer-events-none select-none`}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            textShadow: "0 0 8px rgba(114,40,141,0.6)",
          }}
          animate={{
            y: [0, -100, 0],
            opacity: [0, 1, 0],
            scale: [0.8, 1, 0.8],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          {particle.type.icon}
        </motion.div>
      ))}

      {/* Premium purple flares */}
      {flareParticles.map((flare) => (
        <motion.div
          key={`flare-${flare.id}`}
          className="absolute rounded-full"
          style={{
            left: `${flare.x}%`,
            top: `${flare.y}%`,
            width: `${flare.size}px`,
            height: `${flare.size}px`,
            background: `radial-gradient(circle, ${flare.color} 0%, transparent 70%)`,
            boxShadow: `0 0 10px 5px ${flare.color}30`,
          }}
          animate={{
            opacity: [0, 0.8, 0],
            scale: [0.5, 1.5, 0.5],
          }}
          transition={{
            duration: 3 + Math.random() * 4,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Luxury lens flares */}
      {lensFlares.map((lens) => (
        <motion.div
          key={`lens-${lens.id}`}
          className="absolute"
          style={{
            left: `${lens.x}%`,
            top: `${lens.y}%`,
            width: `${lens.width}px`,
            height: `${lens.height}px`,
            background: `linear-gradient(90deg, transparent 0%, ${lens.color}30 50%, transparent 100%)`,
            transform: `rotate(${lens.rotation}deg)`,
          }}
          animate={{
            opacity: [0, 0.7, 0],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: 4 + Math.random() * 4,
            repeat: Infinity,
            delay: Math.random() * 10,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// Premium Background Component
const PremiumBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions
    const setCanvasDimensions = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasDimensions();
    window.addEventListener("resize", setCanvasDimensions);

    // Create gradient background
    const createGradient = () => {
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, "#FFFFFF"); // White at top
      gradient.addColorStop(0.3, "#F8F4FB"); // Very light purple
      gradient.addColorStop(0.7, "#F4F0F8"); // Light purple
      gradient.addColorStop(1, "#F0EBF5"); // Slightly more purple at bottom
      return gradient;
    };

    // Draw spotlight effects
    const drawSpotlights = (time) => {
      // Main spotlight
      const centerX = canvas.width / 2;
      const centerY = canvas.height * 0.3;
      const radius = canvas.width * 0.4;

      const gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        radius,
      );
      gradient.addColorStop(0, "rgba(114, 40, 141, 0.08)"); // Purple at center
      gradient.addColorStop(0.5, "rgba(103, 32, 163, 0.05)"); // Darker purple
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)"); // Transparent at edges

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fill();

      // Secondary spotlights
      const numSpots = 3;
      for (let i = 0; i < numSpots; i++) {
        const angle = time / 2000 + (i * Math.PI * 2) / numSpots;
        const distance = canvas.width * 0.3;
        const x = centerX + Math.cos(angle) * distance;
        const y = centerY + Math.sin(angle) * distance * 0.5;
        const spotRadius = canvas.width * 0.15;

        const spotGradient = ctx.createRadialGradient(
          x,
          y,
          0,
          x,
          y,
          spotRadius,
        );

        if (i % 3 === 0) {
          spotGradient.addColorStop(0, "rgba(114, 40, 141, 0.05)");
          spotGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        } else if (i % 3 === 1) {
          spotGradient.addColorStop(0, "rgba(109, 35, 152, 0.04)");
          spotGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        } else {
          spotGradient.addColorStop(0, "rgba(132, 56, 102, 0.03)");
          spotGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        }

        ctx.fillStyle = spotGradient;
        ctx.beginPath();
        ctx.arc(x, y, spotRadius, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    // Draw light beams
    const drawLightBeams = (time) => {
      const numBeams = 5;
      const beamWidth = canvas.width * 0.05;
      const beamLength = canvas.width * 1.5;

      for (let i = 0; i < numBeams; i++) {
        const angle =
          time / 5000 + (i * Math.PI) / (numBeams - 1) - Math.PI / 2;
        const centerX = canvas.width / 2;
        const centerY = canvas.height * 1.1;

        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(angle);

        const beamGradient = ctx.createLinearGradient(0, 0, 0, -beamLength);

        if (i % 3 === 0) {
          beamGradient.addColorStop(0, "rgba(114, 40, 141, 0.08)");
          beamGradient.addColorStop(1, "rgba(114, 40, 141, 0)");
        } else if (i % 3 === 1) {
          beamGradient.addColorStop(0, "rgba(103, 32, 163, 0.06)");
          beamGradient.addColorStop(1, "rgba(103, 32, 163, 0)");
        } else {
          beamGradient.addColorStop(0, "rgba(109, 35, 152, 0.05)");
          beamGradient.addColorStop(1, "rgba(109, 35, 152, 0)");
        }

        ctx.fillStyle = beamGradient;
        ctx.beginPath();
        ctx.rect(-beamWidth / 2, 0, beamWidth, -beamLength);
        ctx.fill();

        ctx.restore();
      }
    };

    // Draw subtle grid
    const drawGrid = (time) => {
      const gridSize = 50;
      const gridOpacity = 0.05;

      ctx.strokeStyle = `rgba(114, 40, 141, ${gridOpacity})`;
      ctx.lineWidth = 0.5;

      // Horizontal lines
      for (let y = 0; y < canvas.height; y += gridSize) {
        const waveOffset = Math.sin(y / 200 + time / 2000) * 5;

        ctx.beginPath();
        ctx.moveTo(0, y + waveOffset);

        for (let x = 0; x < canvas.width; x += 10) {
          const waveY = y + waveOffset + Math.sin(x / 200 + time / 3000) * 3;
          ctx.lineTo(x, waveY);
        }

        ctx.stroke();
      }

      // Vertical lines with less frequency
      for (let x = 0; x < canvas.width; x += gridSize * 2) {
        ctx.beginPath();
        ctx.moveTo(x, 0);

        for (let y = 0; y < canvas.height; y += 10) {
          const waveX = x + Math.sin(y / 200 + time / 2000) * 3;
          ctx.lineTo(waveX, y);
        }

        ctx.stroke();
      }
    };

    // Animation loop
    let animationId;
    const animate = (time) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw base gradient
      ctx.fillStyle = createGradient();
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw grid
      drawGrid(time);

      // Draw light beams
      drawLightBeams(time);

      // Draw spotlights
      drawSpotlights(time);

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", setCanvasDimensions);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <>
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />
      {/* Additional overlay effects */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-white/30 to-transparent" />
      {/* Vignette effect */}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_center,_transparent_50%,_rgba(0,0,0,0.4)_100%)]" />
    </>
  );
};

// OTP Input Component
const OtpInput = ({ length = 6, onComplete }) => {
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    if (value && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }

    const otpValue = newOtp.join("");
    if (otpValue.length === length) {
      onComplete(otpValue);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("text")
      .slice(0, length)
      .split("");
    const newOtp = [...otp];

    pasteData.forEach((value, index) => {
      if (index < length && !isNaN(value)) {
        newOtp[index] = value;
        if (inputRefs.current[index]) {
          inputRefs.current[index].value = value;
        }
      }
    });

    setOtp(newOtp);

    const lastFilledIndex = Math.min(pasteData.length - 1, length - 1);
    if (inputRefs.current[lastFilledIndex]) {
      inputRefs.current[lastFilledIndex].focus();
    }

    const otpValue = newOtp.join("");
    if (otpValue.length === length) {
      onComplete(otpValue);
    }
  };

  return (
    <div className="flex justify-between gap-2">
      {otp.map((digit, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          className="w-full"
        >
          <input
            ref={(ref) => (inputRefs.current[index] = ref)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            className="w-full aspect-square rounded-lg border-2 border-[#72288D]/30 bg-gray-100/50 text-center text-2xl font-bold text-[#2D0A4A] outline-none transition-all focus:border-[#72288D] focus:bg-gray-100/80 focus:ring-2 focus:ring-[#72288D]/20"
          />
        </motion.div>
      ))}
    </div>
  );
};

const Login = () => {
  const [toOtp, settoOtp] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setotp] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [code, setCode] = useState("+91");
  const [config, setConfig] = useState([]);
  const [otpId, setOtpId] = useState(null); // Track OTP session
  const [isProcessing, setIsProcessing] = useState(false);

  const isProcessingCallback = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    // When login page is hit by an unregistered/incomplete user or from signup back, clear all tokens and storage
    const isRegistered = localStorage.getItem("isUserRegistered");
    if (isRegistered !== "true") {
      console.log("[Login] Login page hit with unverified session - clearing tokens and storage");
      AuthService.clearTokens();
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {
        console.warn("Storage clear error on login hit:", e);
      }
    }
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.PARTNER_CONFIG, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error("Failed to fetch data");

        const data = await response.json();
        console.log(data);
        setConfig(data);
        localStorage.setItem("TimerTime", data?.notification_miliseconds);
        localStorage.setItem("female_announcement", data?.female_announcement);
        localStorage.setItem("male_announcement", data?.male_announcement);
      } catch (error) {
        console.error("Error fetching songs:", error);
      }
    };
    fetchConfig();
  }, []);

  const handleAuthentication = async (verifyResponse) => {
    console.log("Authentication response:", verifyResponse);

    if (isProcessingCallback.current) return;
    isProcessingCallback.current = true;

    if (!verifyResponse.success) {
      showOtpToast("Invalid OTP. Please try again.");
      return;
    }

    try {
      // Use AuthService to handle verify OTP response - handles all token management
      const sessionData = await AuthService.handleVerifyOtpResponse(verifyResponse);

      // Extract role from userData
      const userRole = sessionData?.role || [];

      // ADDED: Redirect Waiter immediately after login
      if (userRole.includes("Waiter")) {
        navigate("/Waiter");
        return;
      }

      const isBusinessUser = sessionData.isBusinessUser === true;
      localStorage.setItem("isBusiness", JSON.stringify(isBusinessUser));

      if (isBusinessUser) {
        const venuesRes = await fetchWithAuth(API_ENDPOINTS.VENUE_PERFORMERS, {
          method: "GET",
        });
        if (!venuesRes.ok) {
          throw new Error("Failed to fetch venues");
        }
        const venuesData = await venuesRes.json();
        console.log("venuesData", venuesData);
        if (
          !Array.isArray(venuesData.venues) ||
          venuesData.venues.length === 0
        ) {
          showOtpToast(
            "No business venues found. Please use a personal account.",
          );
          // Reset to phone number input phase
          settoOtp(false);
          setPhoneNumber("");
          setotp("");
          AuthService.clearTokens();
          return;
        }
      }
      // 3️⃣ Final redirect based on registration flag
      const registered = sessionData.isUserRegistered === true;
      console.log("Final session data:", sessionData, "registered:", registered);
      localStorage.setItem("PerformerId", sessionData.userId);
      localStorage.setItem("isKj", Boolean(userRole.includes("KJ")));
      localStorage.setItem("isUserRegistered", registered.toString());
      if (!registered) {
        let keepMinimalSignup = false;
        try {
          const configRes = await fetch(API_ENDPOINTS.PARTNER_CONFIG);
          if (configRes.ok) {
            const configData = await configRes.json();
            keepMinimalSignup = configData?.keepMinimalSignup === true;
            localStorage.setItem("keepMinimalSignup", JSON.stringify(keepMinimalSignup));
          }
        } catch (e) {
          console.warn("Error checking partner config:", e);
        }

        if (keepMinimalSignup && isBusinessUser) {
          navigate("/businesssignup");
        } else {
          navigate("/artistsignup");
        }
      } else {
        navigate(
          isBusinessUser ? "/loggedinartistbussiness" : "/loggedinartist",
        );
      }
    } catch (err) {
      console.error(err);
      showOtpToast(err.message || "An error occurred. Please try again.");
    } finally {
      isProcessingCallback.current = false;
    }
  };

  useEffect(() => {
    // Simple initialization - no OTPless script loading needed
    return () => {
      console.log("Cleaning up Login component");
    };
  }, []);

  const handleNumchange = (e) => {
    const value = e.target.value; // Remove non-numeric characters
    setPhoneNumber(value);

    // console.log(value);
    // console.log(phoneNumber);
  };

  const sendOTP = useCallback(async () => {
    if (phoneNumber.length !== 10) {
      alert("Please enter a valid 10-digit phone number.");
      return;
    }

    setIsProcessing(true);
    localStorage.setItem("number", phoneNumber);

    try {
      // Format phone number: remove + from country code and combine
      const formattedPhoneNumber = code.replace('+', '') + phoneNumber;

      const response = await fetch(API_ENDPOINTS.SEND_OTP, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phoneNumber: formattedPhoneNumber,
          appId: "partner",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to send OTP");
      }

      const data = await response.json();
      console.log("OTP sent successfully:", data);

      if (data.success) {
        setOtpId(data.otpId);
        settoOtp(true);
        setCountdown(59);
      } else {
        throw new Error("OTP send failed");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      showOtpToast("Failed to send OTP. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  }, [phoneNumber, code]);

  useEffect(() => {
    if (phoneNumber.length === 10) {
      sendOTP();
      // console.log(phoneNumber);
      localStorage.setItem("number", phoneNumber);
    }
  }, [phoneNumber, sendOTP]);

  const verifyOTP = async (value) => {
    const otpValue = value || otp;
    if (otpValue.length !== 6) {
      console.error("Invalid OTP length:", otpValue.length);
      showOtpToast("Please enter a valid OTP.");
      return;
    }

    if (!otpId) {
      console.error("No OTP session found");
      showOtpToast("Please request OTP again.");
      return;
    }

    setIsProcessing(true);

    try {
      // Format phone number: remove + from country code and combine
      const formattedPhoneNumber = code.replace('+', '') + phoneNumber;

      const response = await fetch(API_ENDPOINTS.VERIFY_OTP, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phoneNumber: formattedPhoneNumber,
          otpCode: otpValue,
          appId: "partner",
          platform: "browser",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to verify OTP");
      }

      const data = await response.json();
      console.log("OTP verified successfully:", data);

      if (data.success) {
        // The verify OTP response contains all the session data
        await handleAuthentication(data);
      } else {
        throw new Error("OTP verification failed");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      showOtpToast("Invalid OTP. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer); // Cleanup function
    }
  }, [countdown]);

  function changeCode(code) {
    // console.log(code);
    setCode(code);
  }


  const resendOTP = async () => {
    if (countdown > 0 || isProcessing) {
      return;
    }

    setIsProcessing(true);

    try {
      // Format phone number: remove + from country code and combine
      const formattedPhoneNumber = code.replace('+', '') + phoneNumber;

      const response = await fetch(API_ENDPOINTS.RESEND_OTP, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phoneNumber: formattedPhoneNumber,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to resend OTP");
      }

      const data = await response.json();
      console.log("OTP resent successfully:", data);

      if (data.success) {
        setOtpId(data.otpId);
        setCountdown(59);
        setotp(""); // Clear the OTP input
      } else {
        throw new Error("OTP resend failed");
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      showOtpToast("Failed to resend OTP. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Modified resend OTP handler
  const handleResendOTP = () => {
    if (countdown === 0) {
      resendOTP();
    }
  };

  // Modified OTP input handler to auto-verify
  const handleOtpComplete = (value) => {
    setotp(value);
    if (value.length === 6) {
      verifyOTP(value);
    }
  };

  // Toast for OTP errors
  const showOtpToast = (msg) => {
    toast.error(msg, {
      position: "top-center",
      autoClose: 3500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
      style: {
        fontWeight: 600,
        fontSize: "1.1rem",
        borderRadius: "10px",
        background: "#fff0f3",
        color: "#b91c1c",
        boxShadow: "0 4px 24px 0 rgba(114,40,141,0.10)",
      },
      icon: "❌",
    });
  };
  return (
    <div className="fixed inset-0 min-h-screen w-full flex items-center justify-center overflow-hidden">
      <ToastContainer />

      {/* Background Elements */}
      <PremiumBackground />
      <ParticleBackground />
      <WaveAnimation />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#72288D]/5 pointer-events-none" />

      {/* Main Content - Centered Modal */}
      <div className="relative z-10 flex flex-col items-center justify-center w-full h-full md:px-4 px-3">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="md:mb-12 mb-2 text-center"
        >
          <motion.div
            className="relative px-3 mb-4 flex items-center justify-center gap-2"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            {/* Purple glow behind logo */}
            <motion.div
              className="absolute -inset-6 -z-10 rounded-full"
              animate={{
                opacity: [0, 0.6, 0],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                repeat: Infinity,
                duration: 3,
                ease: "easeInOut",
              }}
              style={{
                background:
                  "radial-gradient(circle, rgba(114,40,141,0.3) 0%, rgba(114,40,141,0) 70%)",
              }}
            />
            {/* Cylindrical border with animated shadow */}
            <motion.div
              className="absolute -inset-4 -z-10 rounded-full mx-2"
              animate={{
                boxShadow: [
                  "0 0 10px 2px rgba(114,40,141,0.3)",
                  "0 0 20px 5px rgba(114,40,141,0.5)",
                  "0 0 10px 2px rgba(114,40,141,0.3)",
                ],
              }}
              transition={{
                repeat: Infinity,
                duration: 3,
                ease: "easeInOut",
              }}
              style={{
                border: "2px solid rgba(114,40,141,0.2)",
                background:
                  "linear-gradient(145deg, rgba(114,40,141,0.05), rgba(103,32,163,0.05))",
              }}
            />
            <img
              src="/images/logo2.svg"
              alt="Tringbox Logo"
              className="md:h-10 h-5 mb-2 text-[#72288D]"
            />
            <h1 className="md:text-5xl xs:text-3xl text-2xl font-bold text-[#2D0A4A]">
              Tringbox{" "}
              <span className="bg-gradient-to-r from-[#72288D] to-[#6720A3] bg-clip-text text-transparent">
                Partner
              </span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "100%" }}
            transition={{ duration: 1, delay: 1.5, ease: "easeOut" }}
            className="overflow-hidden text-xl text-[#4A4A4A]"
          >
            <span className="inline-block whitespace-nowrap md:text-xl text-sm">
              Control the Sound. Own the Crowd.
            </span>
          </motion.p>
        </motion.div>

        {/* Login Form */}

        {/* Login Form */}
        <AnimatePresence mode="wait">
          {!toOtp ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-md rounded-2xl bg-gray-100/95 p-8 backdrop-blur-xl shadow-xl"
            >
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="md:text-2xl sm:text-xl text-lg font-bold text-[#2D0A4A]">
                    Enter your phone number
                  </h2>
                  <p className="md:text-base text-sm mt-2 text-[#4A4A4A]">
                    We'll send you a one-time password
                  </p>
                </div>

                {/* Phone Input with Business Indicator */}
                <div className="relative">
                  <div className="flex">
                    <select
                      value={code}
                      onChange={(e) => changeCode(e.target.value)}
                      className="rounded-l-lg border border-[#72288D]/20 bg-gray-100 py-3 pl-3 pr-2 text-[#2D0A4A] focus:border-[#72288D] focus:outline-none"
                    >
                      <option value="+91">+91</option>
                      <option value="+1">+1</option>
                    </select>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#72288D]" />
                      <input
                        type="number"
                        value={phoneNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,10}$/.test(value)) {
                            handleNumchange(e);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (["e", "E", ".", "-", "+"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()}
                        className="w-full rounded-r-lg border border-[#72288D]/20 bg-gray-100 py-3 pl-10 pr-4 text-[#2D0A4A] placeholder-[#4A4A4A]/50 focus:border-[#72288D] focus:outline-none"
                        placeholder="Enter 10 digit number"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => sendOTP()}
                  disabled={phoneNumber.length < 10 || isProcessing}
                  className={`w-full rounded-lg bg-[#72288D] py-3 text-center font-medium text-white transition-all hover:bg-[#72288D]/90 ${
                    phoneNumber.length < 10 || isProcessing ? "opacity-50" : ""
                  }`}
                >
                  {isProcessing ? "Sending..." : "Send OTP"}
                </motion.button>

                {/* Terms */}
                <div className="text-center text-sm">
                  <p className="text-[#4A4A4A]/70">
                    By registering, you agree to our{" "}
                    <button
                      onClick={() =>
                        window.open(config?.privacyPolicyLink, "_blank")
                      }
                      className="text-[#72288D] hover:underline"
                    >
                      Terms of Use
                    </button>{" "}
                    &{" "}
                    <button
                      onClick={() =>
                        window.open(config?.privacyPolicyLink, "_blank")
                      }
                      className="text-[#72288D] hover:underline"
                    >
                      Privacy Policy
                    </button>
                  </p>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-md rounded-2xl bg-gray-100/95 p-8 backdrop-blur-xl shadow-xl"
            >
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-[#2D0A4A]">
                    Verify Your Number
                  </h2>
                  <div className="mt-2 flex items-center justify-center gap-2 text-[#4A4A4A]">
                    <span>
                      {code} {phoneNumber}
                    </span>
                    <button
                      onClick={() => settoOtp(false)}
                      className="rounded-full p-1 hover:bg-[#72288D]/10 transition-colors"
                    >
                      <Pencil className="h-4 w-4 text-[#72288D]" />
                    </button>
                  </div>
                </div>

                <OtpInput length={6} onComplete={handleOtpComplete} />

                <div className="flex justify-between items-center text-sm">
                  <span className="text-[#4A4A4A]">0:{countdown}</span>
                  <button
                    onClick={handleResendOTP}
                    disabled={countdown > 0 || isProcessing}
                    className={`text-[#72288D] hover:underline ${
                      countdown > 0 || isProcessing ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    Resend OTP
                  </button>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={verifyOTP}
                  disabled={otp.length !== 6 || isProcessing}
                  className={`w-full rounded-lg bg-[#72288D] py-3 text-center font-medium text-white transition-all hover:bg-[#72288D]/90 ${
                    otp.length !== 6 || isProcessing ? "opacity-50" : ""
                  }`}
                >
                  {isProcessing ? "Verifying..." : "Verify OTP"}
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Login;
